import { __awaiter } from "tslib";
import { Prover } from 'barretenberg-es/client_proofs/prover';
import { Crs } from 'barretenberg-es/crs';
import { PooledFft } from 'barretenberg-es/fft';
import { SinglePippenger } from 'barretenberg-es/pippenger';
import { StandardComposer } from 'barretenberg-es/standard_composer';
import { BarretenbergWasm } from 'barretenberg-es/wasm';
import { WorkerPool } from 'barretenberg-es/wasm/worker_pool';
import createDebug from 'debug';
import { EventEmitter } from 'events';
import { DexieDatabase } from './database';
const debug = createDebug('bb:app');
const getMinPowerOfTwo = (num) => {
    let p = num - 1;
    p |= p >> 1;
    p |= p >> 2;
    p |= p >> 4;
    p |= p >> 8;
    p |= p >> 16;
    return p + 1;
};
export class App extends EventEmitter {
    constructor() {
        super(...arguments);
        this.db = new DexieDatabase('plonk');
        this.circuitSize = 0;
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.barretenberg = yield BarretenbergWasm.new();
            this.pool = new WorkerPool();
            yield this.pool.init(this.barretenberg.module, Math.min(navigator.hardwareConcurrency || 1, 8));
            yield this.createProvingData(4);
        });
    }
    createProvingData(circuitSize) {
        return __awaiter(this, void 0, void 0, function* () {
            const numPoints = getMinPowerOfTwo(circuitSize);
            if (numPoints <= this.circuitSize) {
                return;
            }
            if (this.pippenger) {
                yield this.pippenger.destroy();
            }
            if (this.fft) {
                yield this.fft.destroy();
            }
            debug(`Creating data with circuit size ${numPoints}...`);
            let crsData = yield this.db.getKey(`crs-${numPoints}`);
            let g2Data = yield this.db.getKey(`crs-g2-${numPoints}`);
            if (!crsData || !g2Data) {
                debug('downloading crs data...');
                const crs = new Crs(numPoints);
                yield crs.download();
                crsData = crs.getData();
                yield this.db.addKey(`crs-${numPoints}`, Buffer.from(crsData));
                g2Data = crs.getG2Data();
                yield this.db.addKey(`crs-g2-${numPoints}`, Buffer.from(g2Data));
                debug('done.');
            }
            const [worker] = this.pool.workers;
            this.pippenger = new SinglePippenger(worker);
            yield this.pippenger.init(crsData);
            this.fft = new PooledFft(this.pool);
            yield this.fft.init(numPoints);
            const prover = new Prover(worker, this.pippenger, this.fft, 'STANDARD');
            this.composer = new StandardComposer(this.barretenberg, prover, g2Data);
            this.circuitSize = numPoints;
        });
    }
    getCommands(count) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.getCommands(count);
        });
    }
    addCommand(command) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.addCommand(command);
        });
    }
    deleteOldCommands(maxId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.deleteOldCommands(maxId);
        });
    }
    getAllComposers() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.getAllComposers();
        });
    }
    getComposers(count) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.getComposers(count);
        });
    }
    getComposer(id) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.getComposer(id);
        });
    }
    getComposerWithAlias(alias) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.getComposerWithAlias(alias);
        });
    }
    addComposer(constraintSystem, alias = '') {
        return __awaiter(this, void 0, void 0, function* () {
            const circuitSize = yield this.composer.getCircuitSize(constraintSystem);
            return this.db.addComposer(constraintSystem, circuitSize, alias);
        });
    }
    renameComposer(alias, newAlias) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.renameComposer(alias, newAlias);
        });
    }
    deleteComposer(alias) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.deleteComposer(alias);
        });
    }
    deleteAllComposers() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.deleteAllComposers();
        });
    }
    getProofsForComposer(composerId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.getProofsForComposer(composerId);
        });
    }
    getProof(pid) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.getProof(pid);
        });
    }
    addProof(proof) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.db.addProof(proof);
        });
    }
    updateProofResult(pid, verified, publicInputs) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.updateProofResult(pid, verified, publicInputs);
        });
    }
    deleteProof(pid) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.deleteProof(pid);
        });
    }
    deleteProofsOfComposer(composerId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.deleteProofsOfComposer(composerId);
        });
    }
    deleteAllProofs() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.db.deleteAllProofs();
        });
    }
    prove(composer, witness) {
        return __awaiter(this, void 0, void 0, function* () {
            const { circuitSize, constraintSystem } = composer;
            yield this.createProvingData(circuitSize);
            return this.composer.createProof(constraintSystem, witness);
        });
    }
    verify(composer, proofData, publicInputs) {
        return __awaiter(this, void 0, void 0, function* () {
            const { circuitSize, constraintSystem } = composer;
            yield this.createProvingData(circuitSize);
            return this.composer.verify(constraintSystem, proofData, publicInputs);
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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